import type { CSSProperties, Ref } from 'react'
import { formatShanghaiDate } from '@/const/date.ts'
import { cn } from '@/utils/index.ts'
import type { GroupDetailData } from './finance.types.ts'
import { snapshotObject } from './utils/finance-snapshot.ts'
import { RECEIPT_COLUMNS, RECEIPT_NOTICE, receiptCells, receiptPrice } from './utils/receipt-data.ts'

export function ReceiptDocument({
  group,
  qrUrl,
  ref
}: {
  group: GroupDetailData
  qrUrl?: string
  ref?: Ref<HTMLElement>
}) {
  const customer = snapshotObject(group.customerSnapshot)
  const account = snapshotObject(group.accountSnapshot)
  return (
    <article
      ref={ref}
      className="mx-auto w-[72rem] max-w-none bg-white p-10 font-sans text-sm leading-6 text-black print:w-full print:break-after-page print:p-4 print:last:break-after-auto"
    >
      <header className="mb-6 flex items-start justify-between border-b-2 border-black pb-5">
        <div>
          <p className="text-xs tracking-widest">COLLECTION NOTICE</p>
          <h2 className="mt-2 text-3xl font-semibold tracking-widest">收款通知单</h2>
        </div>
        <dl className="grid grid-cols-[5em_max-content] text-xs tabular-nums">
          <dt className="text-right">收款单号：</dt>
          <dd>{group.groupNo}</dd>
          <dt className="text-right">出单日期：</dt>
          <dd>{group.issuedAt ? formatShanghaiDate(group.issuedAt) : '—'}</dd>
          {group.billingMonth && (
            <>
              <dt className="text-right">归属月份：</dt>
              <dd>{group.billingMonth}</dd>
            </>
          )}
        </dl>
      </header>
      {group.status === 'voided' && (
        <p className="mb-4 border-2 border-red-700 p-3 font-bold text-red-700">
          已作废 · 仅供历史核对 {group.voidReason}
        </p>
      )}
      <div className="mb-4 grid grid-cols-2 gap-3">
        <p>客户名称：{String(customer.name ?? group.customerName)}</p>
        <p>收款单位：{String(account.accountHolder ?? '—')}</p>
      </div>
      <table className="w-full table-fixed border-collapse text-xs [&_td]:border [&_td]:border-gray-300 [&_td]:p-2 [&_td]:align-top [&_td]:break-words [&_th]:border [&_th]:border-gray-300 [&_th]:bg-gray-100 [&_th]:p-2 [&_th]:text-left [&_tr]:break-inside-avoid">
        <colgroup>
          {RECEIPT_COLUMNS.map((column) => (
            <col
              key={column.title}
              className="w-(--receipt-column-width)"
              // 运行时读取与 PDF 共用的列配置，用 CSS 变量传入百分比，避免维护第二套宽度。
              style={{ '--receipt-column-width': column.width } as CSSProperties}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            {RECEIPT_COLUMNS.map((column) => (
              <th key={column.title}>{column.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {group.items.map((item) => (
            <tr key={item.billId}>
              {receiptCells(item).map((cell, index) => (
                <td
                  key={RECEIPT_COLUMNS[index].title}
                  className={cn(
                    'whitespace-pre-wrap',
                    index === 5 && 'whitespace-nowrap',
                    index >= 6 && 'text-right tabular-nums',
                    index === 8 && 'font-medium'
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={RECEIPT_COLUMNS.length - 2}>合计 · {group.items.length} 笔费用</td>
            <td colSpan={2} className="text-right text-base font-bold tabular-nums">
              ¥{receiptPrice(group.totalAmountSnapshot)}
            </td>
          </tr>
        </tfoot>
      </table>
      <div className="mt-8 flex items-start justify-between gap-8">
        <div className="min-w-0 flex-1">
          <p className="font-medium">备注</p>
          <p className="mt-2 whitespace-pre-wrap">{group.note || '—'}</p>
        </div>
        <div className="flex max-w-[60%] items-start gap-5">
          <div className="space-y-1">
            <p>收款单位：{String(account.accountHolder ?? '—')}</p>
            <p>开户银行：{String(account.bankName ?? '—')}</p>
            <p className="break-all">收款账号：{String(account.accountNo ?? '—')}</p>
          </div>
          {qrUrl && <img src={qrUrl} alt="收款二维码" className="h-28 w-28 object-contain" />}
        </div>
      </div>
      <footer className="mt-10 border-t border-gray-300 pt-3 text-xs text-gray-600">{RECEIPT_NOTICE}</footer>
    </article>
  )
}
